import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getConfig } from '@/constants';
import { ClientConfig, UserState } from '@/types/types';
import { AssetTypeEnum } from '@/types/enums';
import { PaletteMode } from '@mui/material';
import i18n from '@/i18n/i18n';

const initialUserState: UserState = {
  status: null,
  siteId: getConfig().SITE_ID ?? null, // only testing
  siteMetadata: {
    name: '',
    assetType: AssetTypeEnum.CAR,
    largeLogoLocation: '',
    logoLocation: '',
    faviconLocation: '',
    mapboxSW: { lat: 0, lon: 0 },
    mapboxNE: { lat: 0, lon: 0 },
    mapboxToken: '',
    mapboxStyle: '',
    featureFlags: {},
    orgCode: '',
  },
  userPosition: null,
  sortedAssetPosition: false,
  tetheredEnabled: false,
  theme: 'light',
  language: i18n.language,
  satelliteView: false,
};

const userSlice = createSlice({
  name: 'user',
  initialState: initialUserState,
  reducers: {
    setSiteIdMetadata(state, action: PayloadAction<ClientConfig>) {
      state.siteMetadata = action.payload;
    },
    setThemeMode(state, action: PayloadAction<PaletteMode>) {
      state.theme = action.payload;
    },
    setSiteId(state, action: PayloadAction<{ siteId: string }>) {
      state.siteId = action.payload.siteId;
      if (getConfig().IS_PLAYWRIGHT) return;
      window?.pendo?.initialize(getPendoMetadata(action.payload.siteId));
    },
    setLanguage(state, action: PayloadAction<string>) {
      state.language = action.payload;
    },
    setUserPositionGlobal(state, action: PayloadAction<any>) {
      state.userPosition = action.payload;
    },
    setTetheredEnabledGlobal(state, action: PayloadAction<boolean>) {
      state.tetheredEnabled = action.payload;
    },
    setSortedAssetPositionGlobal(state, action: PayloadAction<boolean>) {
      state.sortedAssetPosition = action.payload;
    },
    setSatelliteView(state, action: PayloadAction<boolean>) {
      state.satelliteView = action.payload;
    },
  },
});

export const getPendoMetadata = (siteId: string) => {
  return {
    visitor: {
      id: `ExternalUser@${siteId}`,
      emailId: `ExternalUser@${siteId}`,
      role: 'External',
    },
    account: {
      id: siteId,
      name: siteId,
    },
  };
};

export const {
  setSiteId,
  setLanguage,
  setSiteIdMetadata,
  setUserPositionGlobal,
  setTetheredEnabledGlobal,
  setSortedAssetPositionGlobal,
  setThemeMode,
  setSatelliteView,
} = userSlice.actions;

export default userSlice.reducer;
